/**
 * Reading one pasted author name and writing it in APA 7, MLA 9 and Chicago 18 form.
 *
 * Three things a reference list needs that a "last word is the surname" parser gets wrong:
 * a comma-separated suffix ("Martin Luther King, Jr."), honorifics and degrees that style
 * guides leave out ("Dr. Jane Doe", "Jane Doe, PhD"), and group authors ("World Health
 * Organization"), which are written as they are and never inverted or turned into initials.
 */

export interface ParsedName {
  given: string[];
  last: string;
  suffix: string | null;
  /** A group author, kept exactly as typed. */
  organization?: boolean;
}

const RE_WHITESPACE = /\s+/;
const RE_TRAILING_COMMA = /,+$/;
const RE_HYPHEN = /-/;
const RE_TRAILING_DOT = /\.$/;

const SUFFIXES = new Set([
  "jr",
  "jr.",
  "sr",
  "sr.",
  "ii",
  "iii",
  "iv",
  "v",
  "2nd",
  "3rd",
  "4th",
]);

const PARTICLES = new Set([
  "van",
  "von",
  "de",
  "del",
  "della",
  "der",
  "den",
  "di",
  "da",
  "du",
  "la",
  "le",
  "los",
  "las",
  "ter",
  "ten",
  "op",
  "het",
  "el",
  "al",
  "bin",
  "ibn",
  "af",
  "av",
  "zu",
  "vander",
]);

/** Honorifics APA, MLA and Chicago all leave out of a reference-list name. */
const TITLES = new Set([
  "dr",
  "prof",
  "professor",
  "mr",
  "mrs",
  "ms",
  "miss",
  "mx",
  "sir",
  "dame",
  "rev",
  "reverend",
  "fr",
  "hon",
  "capt",
  "col",
  "gen",
  "lt",
  "sgt",
  "rabbi",
  "imam",
]);

/**
 * Degrees and post-nominals that cannot be a surname or a pair of initials when written
 * after a name, with or without a comma.
 */
const DEGREES = new Set([
  "phd",
  "ph.d",
  "edd",
  "ed.d",
  "dphil",
  "dsc",
  "mba",
  "msc",
  "m.sc",
  "mph",
  "mfa",
  "dds",
  "dvm",
  "esq",
  "frs",
  "cpa",
]);

/**
 * Degrees that are also plausible initials ("Smith, M.D."): dropped only after a comma that
 * follows a natural-order name of two or more words, as in "Jane Doe, MD".
 */
const COMMA_DEGREES = new Set([
  "md",
  "m.d",
  "ma",
  "m.a",
  "ms",
  "m.s",
  "jd",
  "j.d",
  "rn",
  "ba",
  "b.a",
  "bsc",
  "llm",
  "llb",
]);

/** Words that mark a group author: an organisation, institution, company or the like. */
const RE_ORGANIZATION =
  /\b(?:Association|Society|University|College|School|Institute|Institution|Department|Ministry|Organization|Organisation|Company|Corporation|Council|Committee|Commission|Foundation|Center|Centre|Centers|Centres|Agency|Bureau|Board|Authority|Administration|Service|Services|Group|Office|Academy|Museum|Library|Hospital|Federation|Alliance|Union|Fund|Trust|Consortium|Network|Laboratory|Laboratories|Government|Parliament|Congress|Bank|Initiative|Programme|Program|Division|Inc|Ltd|LLC|Nations|Team|Press|Publishing|Publishers|Media|News|Research|Club|Party|Church)\b|&|\d/;

function bare(token: string): string {
  return token
    .toLowerCase()
    .replace(RE_TRAILING_COMMA, "")
    .replace(RE_TRAILING_DOT, "");
}

function isSuffixToken(token: string): boolean {
  return SUFFIXES.has(token.toLowerCase().replace(RE_TRAILING_COMMA, ""));
}

/** Normalize a suffix token to its display form ("jr" → "Jr.", "iii" → "III"). */
function displaySuffix(token: string): string {
  const plain = token.replace(RE_TRAILING_COMMA, "");
  const lower = plain.toLowerCase();
  if (lower === "jr" || lower === "jr.") {
    return "Jr.";
  }
  if (lower === "sr" || lower === "sr.") {
    return "Sr.";
  }
  if (lower === "ii" || lower === "iii" || lower === "iv" || lower === "v") {
    return plain.toUpperCase();
  }
  return plain;
}

/** True when the whole line is a group author rather than a person. */
export function looksLikeOrganization(text: string): boolean {
  return RE_ORGANIZATION.test(text);
}

function stripTitles(tokens: string[]): string[] {
  let start = 0;
  while (start < tokens.length - 1 && TITLES.has(bare(tokens[start]))) {
    start += 1;
  }
  return tokens.slice(start);
}

function stripTrailingDegrees(tokens: string[]): string[] {
  let end = tokens.length;
  while (end > 1 && DEGREES.has(bare(tokens[end - 1]))) {
    end -= 1;
  }
  return tokens.slice(0, end);
}

/** Pull a trailing suffix (Jr., III, ...) off a token list, if present. */
function extractSuffix(tokens: string[]): {
  rest: string[];
  suffix: string | null;
} {
  const lastToken = tokens.at(-1);
  if (
    tokens.length >= 2 &&
    lastToken !== undefined &&
    isSuffixToken(lastToken)
  ) {
    return {
      rest: tokens
        .slice(0, -1)
        .map((t) => t.replace(RE_TRAILING_COMMA, ""))
        .filter(Boolean),
      suffix: displaySuffix(lastToken),
    };
  }
  return { rest: tokens, suffix: null };
}

/** Split natural-order tokens into given names + last name, keeping particles attached. */
function splitNaturalOrder(tokens: string[]): {
  given: string[];
  last: string;
} {
  let lastStart = tokens.length - 1;
  while (lastStart > 0 && PARTICLES.has(tokens[lastStart - 1].toLowerCase())) {
    lastStart -= 1;
  }
  if (lastStart === 0) {
    lastStart = tokens.length - 1;
  }
  return {
    given: tokens.slice(0, lastStart),
    last: tokens.slice(lastStart).join(" "),
  };
}

/** "John Michael Smith", "Martin Luther King Jr." — no comma tells which word is the surname. */
function parseNaturalTokens(
  tokens: string[],
  knownSuffix: string | null
): ParsedName {
  const extracted = extractSuffix(tokens);
  const suffix = knownSuffix ?? extracted.suffix;
  const { rest } = extracted;
  if (rest.length === 1) {
    return { given: [], last: rest[0], suffix };
  }
  const { given, last } = splitNaturalOrder(rest);
  return { given, last, suffix };
}

function splitTokens(text: string): string[] {
  return text.split(RE_WHITESPACE).filter(Boolean);
}

/** Parse a comma form like "Smith, John Michael" or "Smith, John, Jr.". */
function parseCommaForm(parts: string[]): ParsedName {
  const last = parts[0];
  let givenTokens = splitTokens(parts[1]);
  let suffix: string | null = null;
  if (parts.length >= 3 && isSuffixToken(parts[2])) {
    suffix = displaySuffix(parts[2]);
  } else {
    const extracted = extractSuffix(givenTokens);
    givenTokens = extracted.rest;
    suffix = extracted.suffix;
  }
  return { given: givenTokens, last, suffix };
}

/**
 * Split on commas and drop what is not part of the name: degrees after a natural-order
 * name ("Jane Doe, MD"), and honorifics.
 */
function nameParts(line: string): string[] {
  const parts = line
    .split(",")
    .map((p) => splitTokens(p))
    .filter((tokens) => tokens.length > 0);
  if (parts.length === 0) {
    return [];
  }
  const naturalFirst = parts[0].length >= 2;
  const kept = parts.filter(
    (tokens, index) =>
      index === 0 ||
      !(
        tokens.length === 1 &&
        (DEGREES.has(bare(tokens[0])) ||
          (naturalFirst && COMMA_DEGREES.has(bare(tokens[0]))))
      )
  );
  kept[0] = stripTrailingDegrees(stripTitles(kept[0]));
  return kept.map((tokens) => tokens.join(" "));
}

/** Parse one line into a structured name. Auto-detects the "Last, First" comma form. */
export function parseName(line: string): ParsedName | null {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }
  if (looksLikeOrganization(trimmed)) {
    return { given: [], last: trimmed, suffix: null, organization: true };
  }
  const parts = nameParts(trimmed);
  if (parts.length === 0) {
    return null;
  }
  const [first, second] = parts;
  if (parts.length === 1) {
    return parseNaturalTokens(splitTokens(first), null);
  }
  // "Martin Luther King, Jr.": a lone suffix after a natural-order name is no given name.
  if (parts.length === 2 && isSuffixToken(second)) {
    return parseNaturalTokens(splitTokens(first), displaySuffix(second));
  }
  return parseCommaForm(parts);
}

/** "Jean-Michel" → "J.-M.", "John" → "J." */
function initialize(givenName: string): string {
  return givenName
    .split(RE_HYPHEN)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join("-");
}

function withSuffix(base: string, suffix: string | null): string {
  return suffix ? `${base}, ${suffix}` : base;
}

export function formatApa(name: ParsedName): string {
  if (name.organization || name.given.length === 0) {
    return withSuffix(name.last, name.suffix);
  }
  const initials = name.given.map(initialize).join(" ");
  return withSuffix(`${name.last}, ${initials}`, name.suffix);
}

export function formatFullInverted(name: ParsedName): string {
  if (name.organization || name.given.length === 0) {
    return withSuffix(name.last, name.suffix);
  }
  return withSuffix(`${name.last}, ${name.given.join(" ")}`, name.suffix);
}
